import { useState, useEffect, useCallback, useRef } from 'react';
import * as offline from '../../utils/offlineAlertStore';

const PER_PAGE = 20;

// Same filename convention as AlertsPage.parseName / OfflineAlertsPage.parseName.
function parseName(fname) {
  const base = String(fname).replace(/\.(jpg|jpeg|png|mp4)$/i, '');
  const m = base.match(/^([a-z]+)_(.+)_(\d{8})_(\d{6})$/i);
  if (m) {
    const [, type, , date, time] = m;
    const y = date.slice(0, 4), mo = date.slice(4, 6), d = date.slice(6, 8);
    const h = time.slice(0, 2), mi = time.slice(2, 4), s = time.slice(4, 6);
    return { type, date: `${d}/${mo}/${y}`, time: `${h}:${mi}:${s}` };
  }
  return { type: base.split('_')[0] || 'unknown', date: '', time: '' };
}

function CachedThumb({ id }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url;
    let alive = true;
    offline.getThumbObjectUrl(id).then(u => {
      if (!alive) { if (u) URL.revokeObjectURL(u); return; }
      url = u; setSrc(u);
    });
    return () => { alive = false; if (url) URL.revokeObjectURL(url); };
  }, [id]);
  if (!src) return <div className="lm-alert-thumb" />;
  return <img src={src} alt="" className="lm-alert-thumb" />;
}

// Alerts synced to this device while logged in to app.parvekshak.in — the
// same on-device cache /offline-alerts reads (see
// docs/offline-alerts-design.md), surfaced here so they're reachable from
// Local Mode too, with no internet and no server. This is a compact view of
// that same cache, not a separate store.
export default function LocalAlertsTab() {
  const supported = offline.isOfflineCacheSupported();
  const [phase, setPhase] = useState('checking'); // checking | unavailable | ready
  const [alerts, setAlerts] = useState([]);
  const [total, setTotal]   = useState(0);
  const [page, setPage]     = useState(0);
  const busyRef = useRef(false);

  useEffect(() => {
    (async () => {
      if (!supported) { setPhase('unavailable'); return; }
      const inited = await offline.isCacheInitialized();
      if (!inited) { setPhase('unavailable'); return; }
      setPhase('ready');
    })();
  }, [supported]);

  const loadPage = useCallback(async (p) => {
    if (busyRef.current) return;
    busyRef.current = true;
    try {
      const { alerts: items, total: t } = await offline.listCachedAlerts({ page: p, perPage: PER_PAGE });
      setAlerts(prev => (p === 0 ? items : [...prev, ...items]));
      setTotal(t);
      setPage(p);
    } finally { busyRef.current = false; }
  }, []);

  useEffect(() => { if (phase === 'ready') loadPage(0); }, [phase, loadPage]);

  if (phase === 'unavailable') {
    return (
      <div className="lm-panel">
        <h2>Alerts</h2>
        <p className="lm-empty">
          {supported
            ? 'No alerts are saved on this device yet. Log in to the app and turn on "Keep recent alerts on this device" — they’ll show up here even without internet.'
            : 'Saved alerts need a secure (https) connection and aren’t available in this mode.'}
        </p>
      </div>
    );
  }

  if (phase === 'checking') {
    return (
      <div className="lm-panel">
        <h2>Alerts</h2>
        <p className="lm-empty">Checking…</p>
      </div>
    );
  }

  const hasMore = alerts.length < total;
  return (
    <div className="lm-panel">
      <div className="lm-panel-header">
        <h2>Alerts</h2>
        <span className="lm-cam-sub">{total} saved</span>
      </div>
      {alerts.length === 0 ? (
        <p className="lm-empty">No saved alerts yet.</p>
      ) : (
        <ul className="lm-cam-list">
          {alerts.map(a => {
            const m = parseName(a.id);
            return (
              <li key={a.id} className="lm-cam-row">
                <CachedThumb id={a.id} />
                <div className="lm-cam-info">
                  <span className="lm-cam-name">{a.type || m.type}</span>
                  <span className="lm-cam-sub">{m.date} {m.time}</span>
                </div>
              </li>
            );
          })}
        </ul>
      )}
      {hasMore && (
        <button className="lm-link-btn" style={{ marginTop: 10 }} onClick={() => loadPage(page + 1)}>Load more</button>
      )}
    </div>
  );
}
